import { ActionReducerMapBuilder, AsyncThunk } from "@reduxjs/toolkit";

/**
 * Shared shape for one "bucket" of async status in a slice — an admin tab, or
 * (for SlotCalendarDb) one cached product entry. loadingMap / errorMap /
 * successMap follow the loadingMap-is-a-string convention every slice in this
 * app uses; keys are per-operation, and a per-ROW operation keys by id
 * (`decide_${pendingId}`) so acting on one row never disables the rest of a
 * list.
 *
 * Lifted out of dataManagement/uiDb/admin/adminTabState.ts (2026-09-25) so a
 * second slice (SlotCalendarDb) doesn't have to re-paste this. admin/
 * adminTabState.ts now re-exports everything here under its original names —
 * zero behaviour change for the admin section, INCLUDING the `tab` config key
 * below (kept as `tab`, not renamed to something more generic, specifically so
 * every existing `registerAsync(builder, thunk, { tab: ... })` call in
 * admin/approvals.ts, admin/subscriptions.ts and admin/allBusinesses.ts keeps
 * compiling unchanged).
 */
export interface AsyncStatusState {
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

/** Fresh base state for one bucket. A function, not a shared constant, so two
 *  buckets can never end up pointing at the same maps object. */
export const createAsyncStatusState = (): AsyncStatusState => ({
  loadingMap: {},
  errorMap: {},
  successMap: {},
});

/** Every thunk wired through registerAsync rejects with a friendly string, so
 *  the config is pinned rather than left as `any`: with a loose ThunkApiConfig,
 *  TypeScript widens the fulfilled payload to `Returned | undefined` and every
 *  onFulfilled callback below loses its types. */
type RegisterAsyncThunkConfig = { rejectValue: string };

interface RegisterAsyncConfig<S, Returned, ThunkArg> {
  /** Picks this thunk's own bucket out of the slice state. Named `tab` for
   *  backward compatibility with the admin section's existing call sites (see
   *  the file header) - an attacher must only ever reach for its OWN bucket
   *  here, since one slice means one shared Immer draft and nothing else
   *  enforces that boundary. */
  tab: (state: S) => AsyncStatusState;
  /** loading/error/success map key. A function when the key is per-row/range. */
  key: string | ((arg: ThunkArg) => string);
  /** Message parked in loadingMap while in flight (the repo stores copy, not a
   *  boolean, so a spinner can explain itself). */
  loading?: string;
  onFulfilled?: (state: S, payload: Returned, arg: ThunkArg) => void;
  /**
   * Return true to say "handled, don't write errorMap" - used for rejections
   * that are a STATE rather than a failure (e.g. access denied, or a
   * not-yet-configured calendar reported as a 404).
   */
  onRejected?: (state: S, message: string, arg: ThunkArg) => boolean | void;
}

/**
 * Wires the three cases of one async thunk into a slice.
 *
 * Exists because a slice with several independent async caches grows three
 * near-identical cases per thunk; without this, cache #2 would be pasting the
 * same twelve lines for the second time. Bucket-specific behaviour stays
 * explicit in the callbacks.
 */
export function registerAsync<S, Returned, ThunkArg>(
  builder: ActionReducerMapBuilder<S>,
  thunk: AsyncThunk<Returned, ThunkArg, RegisterAsyncThunkConfig>,
  config: RegisterAsyncConfig<S, Returned, ThunkArg>
): void {
  const keyOf = (arg: ThunkArg): string =>
    typeof config.key === "function" ? config.key(arg) : config.key;

  // The `as unknown as S` casts are type-level only: Draft<S> and S are the
  // same object at runtime, so mutations still land on the Immer draft.
  builder
    .addCase(thunk.pending, (state, action) => {
      const bucket = config.tab(state as unknown as S);
      const key = keyOf(action.meta.arg as ThunkArg);
      bucket.loadingMap[key] = config.loading ?? "Loading...";
      bucket.errorMap[key] = null;
      bucket.successMap[key] = false;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      const typed = state as unknown as S;
      const bucket = config.tab(typed);
      const key = keyOf(action.meta.arg as ThunkArg);
      bucket.loadingMap[key] = null;
      bucket.successMap[key] = true;
      config.onFulfilled?.(typed, action.payload as Returned, action.meta.arg as ThunkArg);
    })
    .addCase(thunk.rejected, (state, action) => {
      const typed = state as unknown as S;
      const bucket = config.tab(typed);
      const key = keyOf(action.meta.arg as ThunkArg);
      const message =
        (action.payload as string | undefined) ??
        (action.error as { message?: string } | undefined)?.message ??
        "Something went wrong. Please try again.";
      bucket.loadingMap[key] = null;
      const handled = config.onRejected?.(typed, message, action.meta.arg as ThunkArg);
      if (!handled) bucket.errorMap[key] = message;
    });
}
